/**
 * UI-5a Task 3 — RecordingMiniPlayer.
 *
 * Compact inline audio player for a recordings-table row: a play/pause
 * button plus a thin, display-only progress bar.
 *
 * Streaming uses the blob-fetch pattern (plan §0.B): the `/api/recordings/:id/stream`
 * endpoint requires `Authorization: Bearer <token>`, and a bearer token does
 * NOT ride on a plain `<audio src="...">` request. So we `fetch` the stream
 * with the auth header, turn the response body into a blob URL, and feed that
 * to the `<audio>` element. This mirrors `modules/recordings/RecordingPlayer.tsx`.
 *
 * Plan §0.C: the backend stream has no HTTP Range support, so the progress
 * bar is intentionally read-only — there is no draggable seek (a drag would
 * restart playback from 0). Phase-1 acceptable.
 */
import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { Button } from 'primereact/button'

export interface RecordingMiniPlayerProps {
  recordingId: string
  durationSec: number
}

function RecordingMiniPlayer({
  recordingId,
  durationSec,
}: RecordingMiniPlayerProps): JSX.Element {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    if (!recordingId) return
    let mounted = true
    let revokedUrl: string | null = null
    fetch(`/api/recordings/${recordingId}/stream`, {
      credentials: 'include',
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`stream ${r.status}`)
        const blob = await r.blob()
        if (!mounted) return
        revokedUrl = URL.createObjectURL(blob)
        setAudioUrl(revokedUrl)
      })
      .catch(() => {
        // Loading failure leaves the play button disabled — non-fatal for
        // a table-row mini player.
      })
    return () => {
      mounted = false
      if (revokedUrl) URL.revokeObjectURL(revokedUrl)
    }
  }, [recordingId])

  function togglePlay(): void {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
      setPlaying(false)
    } else {
      void audio.play()
      setPlaying(true)
    }
  }

  function handleTimeUpdate(): void {
    const audio = audioRef.current
    if (!audio || durationSec <= 0) return
    setProgress(Math.min(100, (audio.currentTime / durationSec) * 100))
  }

  function handleEnded(): void {
    setPlaying(false)
    setProgress(0)
  }

  return (
    <div
      className="flex items-center gap-2"
      data-testid="recording-mini-player"
    >
      <Button
        type="button"
        rounded
        text
        size="small"
        icon={playing ? 'pi pi-pause' : 'pi pi-play'}
        aria-label={playing ? 'Pause recording' : 'Play recording'}
        disabled={audioUrl === null}
        onClick={togglePlay}
      />
      <div
        className="h-1 flex-1 overflow-hidden rounded bg-gray-200"
        role="presentation"
      >
        <div
          data-testid="mini-player-progress"
          className="h-full bg-blue-500"
          style={{ width: `${progress}%` }}
        />
      </div>
      {audioUrl !== null && (
        <audio
          ref={audioRef}
          src={audioUrl}
          data-testid="mini-player-audio"
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
        >
          <track kind="captions" />
        </audio>
      )}
    </div>
  )
}

export default RecordingMiniPlayer
